import { useState } from 'react';
import { Check, CircleDashed, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import type { MilestoneTimelineBlock, MilestoneStep } from '../../../types/investigator';
import { PROVENANCE_BADGE_CLASSES, PROVENANCE_DOT_CLASSES } from '../../../config/colors';
import { formatUkDateTime } from '../../../lib/calc';

/**
 * The full order lifecycle, mirroring the Truth Browser's order investigation.
 *
 * The point of difference from `EnrichedTimeline` is that a step which did NOT
 * happen still gets a row. "Was this delivered?" is usually answered by an
 * absence — a delivery stop declared by the TMS while no vehicle was ever
 * assigned — and a timeline drawn only from things that happened cannot show
 * that shape at all.
 *
 * Each status keeps its own colour and word. An inference is never dressed as
 * a confirmation (§13.2 keeps provenance a separate axis; §11 requires the
 * difference between seen, asserted and deduced to stay visible).
 */
const STATUS_STYLE: Record<
  MilestoneStep['status'],
  { label: string; badge: string; dot: string; text: string }
> = {
  // Tier colours from the shared colors module (ticket 15).
  observed: {
    label: 'Observed',
    badge: PROVENANCE_BADGE_CLASSES.observed,
    dot: PROVENANCE_DOT_CLASSES.observed,
    text: 'text-foreground',
  },
  declared: {
    label: 'Declared',
    badge: PROVENANCE_BADGE_CLASSES.declared,
    dot: PROVENANCE_DOT_CLASSES.declared,
    text: 'text-foreground',
  },
  inferred: {
    label: 'Inferred',
    badge: PROVENANCE_BADGE_CLASSES.inferred,
    dot: PROVENANCE_DOT_CLASSES.inferred,
    text: 'text-foreground',
  },
  missing: {
    label: 'Missing',
    badge: 'bg-muted text-muted-foreground',
    dot: 'bg-secondary border-accent',
    text: 'text-muted-foreground',
  },
  contradictory: {
    label: 'Contradictory',
    badge: 'bg-warning/20 text-warning',
    dot: 'bg-warning border-warning',
    text: 'text-foreground',
  },
};

function formatWhen(iso?: string): string {
  if (!iso) return '';
  return formatUkDateTime(iso, iso);
}

function StepRow({ step }: { step: MilestoneStep }) {
  const [expanded, setExpanded] = useState(false);
  const style = STATUS_STYLE[step.status] ?? STATUS_STYLE.missing;
  const hasDetail =
    !!step.best_truth ||
    !!step.partial_delivery ||
    step.evidence_used.length > 0 ||
    step.missing_evidence.length > 0 ||
    step.contradictions.length > 0;

  return (
    <li className="relative pl-8">
      <div className={`absolute left-1.5 top-3.5 w-3 h-3 rounded-full border-2 ${style.dot}`} />
      <div className="bg-card/60 rounded-lg border border-border/50 overflow-hidden mb-1">
        <button
          type="button"
          onClick={() => hasDetail && setExpanded(!expanded)}
          className={`w-full flex items-center gap-2 p-3 text-left ${
            hasDetail ? 'hover:bg-card cursor-pointer' : 'cursor-default'
          }`}
        >
          {hasDetail ? (
            expanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            )
          ) : (
            <span className="w-3.5 flex-shrink-0" />
          )}
          {step.status === 'missing' ? (
            <CircleDashed className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          ) : step.status === 'contradictory' ? (
            <AlertTriangle className="w-3.5 h-3.5 text-warning flex-shrink-0" />
          ) : (
            <Check className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          )}
          <span className={`text-xs font-medium ${style.text}`}>{step.title}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${style.badge}`}>{style.label}</span>
          {step.partial_delivery && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-warning/20 text-warning font-mono"
              title={`POD covers ${step.partial_delivery.delivered_units} of ${step.partial_delivery.total_units} — ${step.partial_delivery.outstanding_units} still to deliver`}
            >
              {step.partial_delivery.delivered_units}/{step.partial_delivery.total_units}
            </span>
          )}
          <span className="ml-auto text-xs text-muted-foreground font-mono whitespace-nowrap">
            {formatWhen(step.occurred_at)}
          </span>
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-0 pl-11 space-y-2 border-t border-border/50">
            {step.summary && (
              <p className="text-xs text-muted-foreground leading-relaxed mt-2">{step.summary}</p>
            )}
            {step.best_truth && (
              <div className="text-xs">
                <span className="text-muted-foreground">Best truth: </span>
                <span className="text-foreground">{step.best_truth}</span>
              </div>
            )}
            {step.partial_delivery && (
              <div className="text-xs">
                <span className="text-muted-foreground">Part delivery: </span>
                <span className="text-warning">
                  POD covers {step.partial_delivery.delivered_units} of{' '}
                  {step.partial_delivery.total_units} — {step.partial_delivery.outstanding_units} still
                  to deliver. The source records the order as delivered.
                </span>
              </div>
            )}
            {step.evidence_used.length > 0 && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-0.5">Evidence used</div>
                <ul className="space-y-0.5">
                  {step.evidence_used.map((e, i) => (
                    <li key={i} className="text-foreground">
                      · {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Never hidden — what was looked for and not found is part of the
                answer, and is usually what "does anything confirm this?" turns on. */}
            {step.missing_evidence.length > 0 && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-0.5">Looked for, not found</div>
                <ul className="space-y-0.5">
                  {step.missing_evidence.map((e, i) => (
                    <li key={i} className="text-warning/90">
                      · {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {step.contradictions.length > 0 && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-0.5">Contradictions</div>
                <ul className="space-y-0.5">
                  {step.contradictions.map((e, i) => (
                    <li key={i} className="text-warning">
                      · {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export default function MilestoneTimeline({ block }: { block: MilestoneTimelineBlock }) {
  if (block.steps.length === 0) return null;

  const counts = block.steps.reduce<Record<string, number>>((acc, s) => {
    acc[s.status] = (acc[s.status] ?? 0) + 1;
    return acc;
  }, {});
  const resolved = block.steps.length - (counts.missing ?? 0);

  return (
    <div className="h-full overflow-y-auto pr-1">
      <div className="flex items-baseline gap-2 mb-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Order lifecycle
        </h3>
        <span className="text-xs text-muted-foreground">
          {resolved}/{block.steps.length} steps resolved
        </span>
      </div>

      {block.declared_status && (
        <p className="text-xs text-muted-foreground mb-2">
          Source system declares this order as{' '}
          <span className="text-foreground font-mono">{block.declared_status}</span>
        </p>
      )}

      <div className="relative">
        <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
        <ul>
          {block.steps.map((step) => (
            <StepRow key={step.code} step={step} />
          ))}
        </ul>
      </div>
    </div>
  );
}
